/**
 * Login helpers shared by the router guard, the 401 interceptor and the /auth/callback view.
 *
 * GET /api/login returns `{ loginURL }` (legacy `?token=` redirect) or, when FEATURE_AUTH_CODE_FLOW
 * is on, `{ loginURL, flow: "code" }` pointing to the authorization-code + PKCE authorize URL.
 */
import axios from "axios";

export const AUTH_CALLBACK_PATH = "/auth/callback";

const STATE_KEY = "authState";
const RETURN_PATH_KEY = "authReturnPath";
const ATTEMPTS_KEY = "authCallbackAttempts";

/** Max automatic login retries from the callback page per browser session (loop guard). */
export const MAX_CALLBACK_ATTEMPTS = 2;

function sessionGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function sessionSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // storage unavailable: server-side state check still applies
  }
}

function sessionRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function isAuthCallbackPath(path: string = window.location.pathname): boolean {
  return path.replace(/\/+$/, "") === AUTH_CALLBACK_PATH;
}

/** Sends the user to the login page (legacy or authorization-code flow, decided by the server). */
export function sendToLogin(): void {
  axios.get("/login").then((res) => {
    const data = (res as unknown) as { loginURL?: string; flow?: string };
    if (!data?.loginURL) return;
    if (data.flow === "code") {
      try {
        const state = new URL(data.loginURL).searchParams.get("state");
        if (state) sessionSet(STATE_KEY, state);
      } catch {
        // malformed URL: let the login page report it
      }
      if (!isAuthCallbackPath()) {
        sessionSet(RETURN_PATH_KEY, window.location.pathname + window.location.search + window.location.hash);
      }
    }
    location.href = data.loginURL;
  });
}

/** Only same-origin absolute paths are allowed as a post-login destination. */
function safeReturnPath(path: string | null): string {
  if (!path || !path.startsWith("/") || path.startsWith("//") || path.startsWith("/\\")) return "/";
  if (isAuthCallbackPath(path.split(/[?#]/)[0])) return "/";
  return path;
}

export type AuthCallbackResult =
  | { ok: true; returnPath: string }
  | { ok: false; reason: "missing_params" | "state_mismatch" | "exchange_failed"; message?: string };

/**
 * Handles /auth/callback?code=&state=: removes the parameters from the address bar, verifies the
 * state against the one stored when the login started, exchanges the code server-side and stores
 * the JWT as `userToken` (same as the legacy `?token=` bootstrap in main.ts).
 */
export async function completeAuthCallback(): Promise<AuthCallbackResult> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");

  // Never keep the code in the address bar or history.
  window.history.replaceState(window.history.state, "", AUTH_CALLBACK_PATH);

  const expectedState = sessionGet(STATE_KEY);
  sessionRemove(STATE_KEY);

  if (!code || !state) return { ok: false, reason: "missing_params" };
  if (!expectedState || expectedState !== state) return { ok: false, reason: "state_mismatch" };

  let data: { token?: string; error?: string } | undefined;
  try {
    // skipSwal: the interceptor returns the body as-is and does not redirect on 401.
    data = ((await axios.post("/auth/exchange", { code, state }, { skipSwal: true })) as unknown) as {
      token?: string;
      error?: string;
    };
  } catch {
    data = undefined;
  }

  if (!data?.token) {
    return { ok: false, reason: "exchange_failed", message: data?.error };
  }

  window.localStorage.setItem("userToken", data.token);
  sessionRemove(ATTEMPTS_KEY);
  const returnPath = safeReturnPath(sessionGet(RETURN_PATH_KEY));
  sessionRemove(RETURN_PATH_KEY);
  return { ok: true, returnPath };
}

/**
 * Records a failed callback and reports whether another automatic login attempt is allowed.
 */
export function registerCallbackFailure(): boolean {
  const attempts = Number(sessionGet(ATTEMPTS_KEY) || "0") + 1;
  sessionSet(ATTEMPTS_KEY, String(attempts));
  return attempts <= MAX_CALLBACK_ATTEMPTS;
}
